import { defineNuxtRouteMiddleware, navigateTo, useState } from 'nuxt/app';
import { resolveSiteGateRedirect } from '~/utils/siteGate';

export default defineNuxtRouteMiddleware((to) => {
  // Both resolved server-side and carried here in the payload — see
  // app/plugins/siteGate.server.ts. The preview cookie is httpOnly and holds
  // the CMS token, so the client cannot and should not inspect it directly.
  // Defaults gate the site rather than open it.
  const gateEnabled = useState<boolean>('siteGate', () => true).value;
  const hasPreviewAccess = useState<boolean>('sitePreview', () => false).value;

  // A URL that matches no route can only render the generic 404 error page,
  // which carries no gated content — so let it 404 instead of redirecting.
  // Without this, a missing root-level static file (which skips the nitro
  // gate as ungateable and falls through to this router) came back as a
  // 302 → /coming-soon → / chain instead of a 404: wrong for users hitting a
  // typo'd asset URL, and a soft-redirect signal for crawlers. Gate state is
  // deliberately NOT touched here — real pages still gate exactly as before,
  // including on client-side navigation away from a 404. (`?.` because the
  // middleware spec drives this handler with bare `{ path }` fixtures; the
  // real router always supplies `matched`.)
  if (to.matched?.length === 0) return;

  const redirect = resolveSiteGateRedirect({
    path: to.path,
    gateEnabled,
    hasPreviewAccess,
  });

  if (!redirect) return;

  // 302, not 301: a permanent redirect would be cached by browsers and
  // CloudFront and would outlive re-opening the gate.
  return navigateTo(redirect, { redirectCode: 302 });
});
